import { useState } from "react";
import { Link, NavLink } from "react-router-dom";

import { OfficialWordmark } from "../../components/OfficialWordmark";

/**
 * Chrome and structural parts shared by the four public pages: landing,
 * pricing, onboarding and about.
 *
 * These used to live inside `LandingPage`, which was fine while there was one
 * public page and wrong the moment there were four. The header's in-page
 * anchor links went with them and did not survive the move on purpose: an
 * `href="#modules"` in shared chrome is a dead link on every page except the
 * one that happens to have that section.
 *
 * On the design: the whole public site is set as a ledger page, which is the
 * identity `theme.css` already names for the app itself. Sections open with a
 * rule, the eyebrow sits on that rule like a column header, and a real figure
 * hangs off the right the way an amount does. Every one of those figures is a
 * fact about this codebase rather than a decorative number, which is the only
 * reason they earn the space.
 */

// ─────────────────────────────────────────────────────────────────────
//  Header and footer
// ─────────────────────────────────────────────────────────────────────

const NAV: readonly { to: string; label: string }[] = [
  { to: "/#modules", label: "Modules" },
  { to: "/pricing", label: "Pricing" },
  { to: "/onboarding", label: "Onboarding" },
  { to: "/about", label: "About" },
];

/**
 * Solid rather than translucent-with-backdrop-blur: a blurred backdrop on a
 * sticky element is a real repaint cost on the low-end Android phones a lot of
 * this audience browses on, and it buys nothing over an opaque bar.
 */
export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="site-header-light sticky top-0 z-40 border-b border-[#c8d2c2] bg-[#eef1e9] text-[#12201f]">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5">
        <Link to="/" aria-label="Wakaskul home">
          <OfficialWordmark className="h-10 sm:h-11" />
        </Link>

        <nav className="hidden items-center gap-0.5 sm:flex sm:gap-2" aria-label="Site">
          {NAV.map((item) => (
            item.to.startsWith("/#") ? (
              <Link key={item.to} to={item.to} className="rounded px-3 py-2 text-sm text-[#12201f]/75 hover:text-[#12201f]">
                {item.label}
              </Link>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded px-3 py-2 text-sm ${
                    isActive
                      ? "font-medium text-[#12201f] underline decoration-[#156064] decoration-2 underline-offset-[6px]"
                      : "text-[#12201f]/75 hover:text-[#12201f]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          ))}
          <Link to="/demo" className="rounded px-3 py-2 text-sm font-medium text-[#156064] hover:underline">
            Demo
          </Link>
          <Link
            to="/login"
            className="rounded bg-[#c9f76f] px-4 py-2 text-sm font-medium text-[#10151f] hover:opacity-90"
          >
            Sign in
          </Link>
        </nav>

        <div className="flex items-center gap-1 sm:hidden">
          <Link to="/demo" className="rounded px-2.5 py-2 text-sm font-medium text-[#156064]">
            Demo
          </Link>
          <Link to="/login" className="rounded bg-[#c9f76f] px-3 py-2 text-sm font-medium text-[#10151f]">
            Sign in
          </Link>
          <button
            type="button"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-site-menu"
            onClick={() => setMenuOpen((open) => !open)}
            className="rounded border border-[#12201f]/30 px-2.5 py-2 text-sm font-medium text-[#12201f]"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 fill-current">
              {[5, 12, 19].flatMap((x) => [5, 12, 19].map((y) => (
                <rect key={`${x}-${y}`} x={x - 1.5} y={y - 1.5} width="3" height="3" rx="0.75" />
              )))}
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-site-menu" className="fixed inset-0 z-50 sm:hidden">
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 h-full w-full bg-black/35"
          />
          <div className="absolute inset-y-0 right-0 flex h-dvh w-[80vw] max-w-sm flex-col bg-[#eef1e9] px-5 py-5 text-[#12201f] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#12201f]/20 pb-4">
              <OfficialWordmark className="h-10" />
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="rounded border border-[#12201f]/30 px-3 py-2 text-sm font-medium text-[#12201f]"
              >
                Close
              </button>
            </div>
            <nav className="mt-6 flex flex-col gap-1" aria-label="Mobile site">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded px-3 py-3 text-base font-medium text-[#12201f]/85 hover:bg-black/5 hover:text-[#12201f]"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/demo"
                onClick={() => setMenuOpen(false)}
                className="rounded px-3 py-3 text-base font-medium text-[#156064] hover:bg-black/5"
              >
                Demo
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-accent text-[#f6f8f0]">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <OfficialWordmark className="h-10" dark />
          <p className="mt-2 text-sm text-[#f6f8f0]/75">
            School management for Ugandan schools. One deployment, many schools, isolated twice
            over.
          </p>
        </div>

        <FooterColumn title="Product">
          <Link to="/pricing" className="hover:underline">
            Pricing
          </Link>
          <Link to="/onboarding" className="hover:underline">
            Onboarding
          </Link>
          <Link to="/demo" className="hover:underline">
            Live demo
          </Link>
          <Link to="/login" className="hover:underline">
            Sign in
          </Link>
        </FooterColumn>

        <FooterColumn title="Company">
          <Link to="/about" className="hover:underline">
            About Wakaskul
          </Link>
          <a href="https://opensigma.cc" target="_blank" rel="noreferrer" className="hover:underline">
            Opensigma Data Solutions
          </a>
          <a href="https://wakaerp.com" target="_blank" rel="noreferrer" className="hover:underline">
            Waka ERP
          </a>
        </FooterColumn>

        <FooterColumn title="Contact">
          <a href="mailto:wakaskul@opensigma.cc" className="hover:underline">
            wakaskul@opensigma.cc
          </a>
          <a href="tel:+256773550009" className="hover:underline">
            +256 773 550009
          </a>
        </FooterColumn>
      </div>

      <div className="border-t border-white/15">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-5 text-xs text-[#f6f8f0]/60">
          <p>© 2026 Wakaskul. Built in East Africa.</p>
          <p className="font-mono">Multi-tenant by design.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.16em] text-[#c9f76f]">
        {title}
      </p>
      <div className="mt-3 flex flex-col gap-1.5 text-sm text-[#f6f8f0]/80">{children}</div>
    </div>
  );
}

/**
 * Wraps a public page so all four share one chrome, one background and one
 * max width. The public site uses the light ledger-paper ground; the optional
 * Midnight tone remains available for focused dark presentations.
 */
export function SitePage({
  children,
  tone = "ledger",
}: {
  children: React.ReactNode;
  tone?: "ledger" | "midnight";
}) {
  return (
    <div className={`min-h-screen overflow-x-hidden bg-paper ${tone === "midnight" ? "site-midnight" : ""}`}>
      <SiteHeader />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Structure
// ─────────────────────────────────────────────────────────────────────

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-ink-faint">{children}</p>
  );
}

/**
 * A section's opening line, set as a ledger entry: a rule across the full
 * measure, the eyebrow sitting on it at the left, and a figure hanging off the
 * right where an amount would be.
 *
 * The figure is not optional decoration. Every caller passes a number that is
 * true of this codebase and checkable, which is the whole point of putting it
 * where a reader expects an audited amount. A section with no such number
 * omits the prop rather than inventing one.
 */
export function SectionHead({
  eyebrow,
  title,
  lede,
  figure,
  tone = "ink",
}: {
  eyebrow: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
  figure?: { value: string; label: string };
  tone?: "ink" | "paper";
}) {
  const ink = tone === "paper" ? "text-paper" : "text-ink";
  const soft = tone === "paper" ? "text-paper/70" : "text-ink-soft";
  const faint = tone === "paper" ? "text-paper/55" : "text-ink-faint";
  const rule = tone === "paper" ? "border-paper/25" : "border-ink/15";

  return (
    <div className={`border-t-2 ${rule} pt-4`}>
      <div className="flex items-start justify-between gap-6">
        <p className={`font-mono text-[0.6rem] uppercase tracking-[0.22em] ${faint}`}>{eyebrow}</p>
        {figure && (
          <p className="text-right">
            <span className={`tabular block font-display text-2xl font-bold leading-none ${ink}`}>
              {figure.value}
            </span>
            <span
              className={`mt-1 block max-w-[11rem] font-mono text-[0.55rem] uppercase leading-relaxed tracking-[0.12em] ${faint}`}
            >
              {figure.label}
            </span>
          </p>
        )}
      </div>

      <h2 className={`display-xl mt-5 max-w-3xl font-display font-bold ${ink}`}>{title}</h2>
      {lede && <p className={`mt-5 max-w-2xl text-lg leading-relaxed ${soft}`}>{lede}</p>}
    </div>
  );
}

/** The one primary action on a page. Sized for a thumb, not a mouse. */
export function PrimaryAction({
  to,
  href,
  children,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
}) {
  const className =
    "inline-flex items-center gap-2 rounded bg-accent px-6 py-3.5 font-display font-bold text-paper hover:opacity-90";

  if (href) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to ?? "/"} className={className}>
      {children}
    </Link>
  );
}

export function SecondaryAction({
  to,
  href,
  children,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
}) {
  const className =
    "inline-flex items-center gap-2 rounded border-2 border-ink px-6 py-3.5 font-display font-bold text-ink hover:bg-ink hover:text-paper";

  if (href) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to ?? "/"} className={className}>
      {children}
    </Link>
  );
}
